/**
 * Camada de integração de PIXEL + TAGS DE CONVERSÃO (estrutura plug-and-play).
 *
 * Arquitetura:
 * - `track()` (tracking.ts) empurra TODO evento pro dataLayer (caminho GTM).
 * - Este módulo adiciona disparo DIRETO nas plataformas (Meta Pixel / GA4 / Google Ads)
 *   para os eventos de CONVERSÃO — funciona mesmo SEM configurar tags no painel GTM.
 * - Tudo é config-gated (só carrega se o ID existir em site.config.tracking) e
 *   consent-gated (só carrega/dispara após consentimento de marketing — LGPD).
 *
 * Para ativar: preencher as env VITE_* (ver site.config.ts). Sem ID → no-op silencioso.
 * IDs suportados: VITE_META_PIXEL_ID, VITE_GA4_ID, VITE_GOOGLE_ADS_ID,
 * VITE_GOOGLE_ADS_LEAD_LABEL, VITE_GOOGLE_ADS_WHATSAPP_LABEL, VITE_GTM_ID.
 */

import { siteConfig } from "@/site.config";

type AnyFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: AnyFn;
    _fbq?: AnyFn;
    gtag?: AnyFn;
  }
}

const cfg = siteConfig.tracking;

/**
 * Mapa: evento interno → conversão em cada plataforma.
 * `meta` = Meta Standard Event · `ga4` = nome do evento GA4 (marcar como conversão no GA4)
 * `adsLabel` = chave em site.config.tracking.googleAdsConversionLabels · `value` = usa payload.value.
 */
export const CONVERSION_MAP: Record<
  string,
  { meta?: string; ga4?: string; adsLabel?: "lead" | "whatsapp"; isConversion?: boolean }
> = {
  form_submitted: {
    meta: "Lead",
    ga4: "generate_lead",
    adsLabel: "lead",
    isConversion: true,
  },
  whatsapp_clicked: {
    meta: "Contact",
    ga4: "contact",
    adsLabel: "whatsapp",
    isConversion: true,
  },
  phone_clicked: { meta: "Contact", ga4: "contact" },
  instagram_clicked: { meta: "Contact", ga4: "contact_instagram" },
  simulator_completed: { meta: "InitiateCheckout", ga4: "simulate" },
  simulator_started: { ga4: "simulate_start" },
  lead_dialog_opened: { meta: "InitiateCheckout", ga4: "begin_checkout" },
  cta_click: { ga4: "cta_click" },
};

let marketingLoaded = false;

function injectScript(src: string, async = true): Promise<void> {
  return new Promise(resolve => {
    if (typeof document === "undefined") return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = async;
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
}

/**
 * Carrega os scripts de marketing (Meta Pixel + GA4/Ads via gtag).
 * Chamado UMA vez quando o usuário consente marketing (ConsentContext).
 * GA4/Ads via gtag só carregam se NÃO houver GTM (evita contagem dupla —
 * com GTM, configure GA4/Ads como tags que consomem o dataLayer).
 */
export function loadMarketingScripts(): void {
  if (marketingLoaded || typeof window === "undefined") return;
  marketingLoaded = true;

  // ---- Meta Pixel (fbq) ----
  if (cfg.metaPixelId) {
    if (!window.fbq) {
      const fbq: AnyFn = function (...args: unknown[]) {
        // @ts-expect-error fila interna do fbq
        fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
      };
      // @ts-expect-error bootstrap padrão do Meta Pixel
      fbq.queue = [];
      // @ts-expect-error
      fbq.loaded = true;
      // @ts-expect-error
      fbq.version = "2.0";
      window.fbq = fbq;
      window._fbq = window._fbq || fbq;
      void injectScript("https://connect.facebook.net/en_US/fbevents.js");
    }
    window.fbq!("init", cfg.metaPixelId);
    window.fbq!("track", "PageView");
  }

  // ---- GA4 + Google Ads (gtag) — só sem GTM ----
  const wantsGtag = (cfg.ga4Id || cfg.googleAdsId) && !cfg.gtmId;
  if (wantsGtag) {
    const primaryId = cfg.ga4Id || cfg.googleAdsId;
    void injectScript(
      `https://www.googletagmanager.com/gtag/js?id=${primaryId}`
    );
    window.dataLayer = window.dataLayer || [];
    const gtag: AnyFn = function (...args: unknown[]) {
      window.dataLayer!.push(args as unknown as Record<string, unknown>);
    };
    window.gtag = window.gtag || gtag;
    window.gtag("js", new Date());
    if (cfg.ga4Id) window.gtag("config", cfg.ga4Id, { anonymize_ip: true });
    if (cfg.googleAdsId) window.gtag("config", cfg.googleAdsId);
  }
}

/** Sanitiza payload p/ o Meta (só chaves simples, sem objetos aninhados). */
function metaParams(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (["string", "number", "boolean"].includes(typeof v)) out[k] = v;
  }
  if (out.value != null) out.currency = "BRL";
  return out;
}

/**
 * Dispara o evento DIRETAMENTE nas plataformas (Meta Pixel + GA4 + Google Ads).
 * Chamado por track() (tracking.ts) após o push no dataLayer. No-op se o ID/script
 * não estiver carregado (sem consent ou sem config).
 */
export function firePlatformEvents(
  event: string,
  payload: Record<string, unknown> = {}
): void {
  if (typeof window === "undefined") return;
  const map = CONVERSION_MAP[event];
  if (!map) return;

  if (window.fbq && map.meta) {
    window.fbq("track", map.meta, metaParams(payload));
  }
  if (window.gtag && map.ga4) {
    window.gtag("event", map.ga4, payload);
  }
  if (window.gtag && map.adsLabel && cfg.googleAdsId) {
    const label = cfg.googleAdsConversionLabels[map.adsLabel];
    if (label) {
      window.gtag("event", "conversion", {
        send_to: `${cfg.googleAdsId}/${label}`,
        value: typeof payload.value === "number" ? payload.value : undefined,
        currency: "BRL",
      });
    }
  }
}
